import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Autocomplete } from '@/modules/common';
import { OptionType } from '@/modules/common/types';
import { StyleItem } from '@/modules/styles/types';

import { StyleTabItem } from '..';
import { addEventStyleSchema } from '../schema';

interface IProps {
  isOpen: boolean;
  stylesData: StyleItem[];
  selectedStyle: StyleTabItem | null;
  onOpenChange: (open: boolean) => void;
  onAdd: (model: StyleTabItem) => void;
  onEdit: (model: StyleTabItem) => void;
}

export type AddEventStyleValues = z.infer<typeof addEventStyleSchema>;

const defaultStyle: StyleTabItem = {
  styleId: '',
};

export function AddEditStyleDialog({
  isOpen,
  onOpenChange,
  onEdit,
  onAdd,
  stylesData,
  selectedStyle,
}: IProps) {
  const isEdit = selectedStyle !== null;

  const form = useForm<AddEventStyleValues>({
    resolver: zodResolver(addEventStyleSchema),
    defaultValues: defaultStyle,
  });

  const { control, handleSubmit, watch, formState: { isDirty } } = form;
  const styleId = watch('styleId');

  const stylesOptions = stylesData?.map((style) => ({
    value: style.name,
    id: style.id,
  }));

  const selectedStyleOption = stylesData?.find(
    (option) => option.id === styleId
  );

  const handleClose = async (open: boolean) => {
    onOpenChange(open);
    form.reset(defaultStyle);
  };

  const handleFormSubmit = async (values: StyleTabItem) => {
    if (isEdit) {
      onEdit(values);
    } else {
      onAdd(values);
    }
    handleClose(false);
  };

  useEffect(() => {
    if (isEdit) {
      form.reset(selectedStyle);
    }
  }, [isEdit]);

  return (
    <>
      <Dialog onOpenChange={handleClose} open={isOpen} modal={false}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add style</DialogTitle>
            <DialogDescription>Description</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleSubmit(handleFormSubmit)(event);
              }}
            >
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-[1fr_auto] gap-4 items-end">
                  <Autocomplete
                    label="Style"
                    name={'styleId'}
                    control={control}
                    searchable
                    placeholder="Select name"
                    options={stylesOptions}
                  />
                  {selectedStyleOption && (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm">Preview</p>
                      <img
                        src={selectedStyleOption.imagePublicUrl}
                        alt={selectedStyleOption.name}
                        className="mb-[2px] w-[40px] h-[40px] rounded-lg"
                      />
                    </div>
                  )}
                </div>

                <FormItem>
                  <FormLabel>Positive Prompt</FormLabel>
                  <FormControl>
                    <Textarea
                      value={
                        selectedStyleOption
                          ? selectedStyleOption?.positivePrompts
                          : ''
                      }
                      placeholder="Enter positive prompt"
                      className="min-h-[80px]"
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
                <FormItem>
                  <FormLabel>Negative Prompt</FormLabel>
                  <FormControl>
                    <Textarea
                      value={
                        selectedStyleOption
                          ? selectedStyleOption.negativePrompts
                          : ''
                      }
                      placeholder="Enter negative prompt"
                      className="min-h-[80px]"
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </div>
              <DialogFooter className="mt-6 gap-4">
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-button">
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  data-testid="save-button"
                  // isLoading={sendSmsMutation.isPending}
                  disabled={!isDirty}
                >
                  {isEdit ? 'Save' : 'Add Style'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
